import React, { useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { spacing, typography } from '../../../core/theme/tokens';
import { APPLE_SIGN_IN_BUTTON_HEIGHT } from '../appleAuthHelpers';
import type { AppleSignInButtonProps } from '../appleAuthTypes';
import { AppleLogo } from './AppleLogo.web';

export type { AppleSignInButtonProps };

/** HIG-compliant black Sign in with Apple — same 52pt height as Google. */
export function AppleSignInButton({
  onPress,
  disabled,
  loading,
  style,
}: AppleSignInButtonProps) {
  const [hovered, setHovered] = useState(false);
  const inactive = Boolean(disabled || loading);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel="Sign in with Apple"
      disabled={inactive}
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={({ pressed }) => [
        styles.pressable,
        inactive && styles.disabled,
        (pressed || hovered) && !inactive && styles.interactive,
        style as StyleProp<ViewStyle>,
      ]}
    >
      <View style={styles.inner}>
        {loading ? (
          <ActivityIndicator color="#FFFFFF" />
        ) : (
          <>
            <View style={styles.logoSlot}>
              <AppleLogo size={18} color="#FFFFFF" />
            </View>
            <Text style={styles.label}>Sign in with Apple</Text>
          </>
        )}
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  pressable: {
    alignSelf: 'stretch',
    borderRadius: 8,
    backgroundColor: '#000000',
    minHeight: APPLE_SIGN_IN_BUTTON_HEIGHT,
    justifyContent: 'center',
    cursor: 'pointer',
    transitionProperty: 'transform, box-shadow, opacity',
    transitionDuration: '160ms',
  } as ViewStyle,
  disabled: {
    opacity: 0.45,
  },
  interactive: {
    opacity: 0.92,
    transform: [{ translateY: -1 }],
  },
  inner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  logoSlot: {
    width: 18,
    height: 18,
    flexGrow: 0,
    flexShrink: 0,
  },
  label: {
    ...typography.headline,
    fontSize: 16,
    color: '#FFFFFF',
  },
});
